import {
    isRejectedWithValue,
    type Middleware,
    type MiddlewareAPI,
} from "@reduxjs/toolkit";
import {
    BaseQueryFn,
    createApi,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { SALE_API_URL } from "../../constants";
import {
    postLogout,
    postSalesLogin,
} from "../../features/authentication/services";
import { getLocalValueByKey } from "../../utils";
import { RootState } from "../store";

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: SALE_API_URL,
    credentials: "include",
    prepareHeaders: (headers, api) => {
        const { getState } = api;
        const isLogin = (getState() as RootState).auth.isLogin;
        const token = getLocalValueByKey("sale_accessToken") || null;

        if (isLogin && token) {
            headers.set("Authorization", "Bearer " + token);
        }
        return headers;
    },
});

const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    // accessToken expired handling
    if (result?.error && result?.error?.status === 401) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const saleRefreshToken = localStorage.getItem("sale_refreshToken") || null;

                if (saleRefreshToken) {
                    try {
                        const res = (await baseQuery(
                            {
                                url: `${SALE_API_URL}/panel_token_refresh`,
                                method: "POST",
                                body: { refresh_token: saleRefreshToken },
                            },
                            api,
                            extraOptions
                        )) as { data: { data: { access_token: string; refresh_token: string } } };

                        const refreshResult = res.data.data;

                        if (refreshResult?.access_token && refreshResult?.refresh_token) {
                            api.dispatch(
                                postSalesLogin({
                                    accessToken: refreshResult?.access_token,
                                    refreshToken: refreshResult?.refresh_token,
                                })
                            );
                            result = await baseQuery(args, api, extraOptions);
                        } else {
                            api.abort();
                            apiSlice.util.resetApiState();
                            api.dispatch(postLogout());
                        }
                    } catch (error) {
                        api.abort();
                        apiSlice.util.resetApiState();
                        api.dispatch(postLogout());
                    }
                } else {
                    api.abort();
                    apiSlice.util.resetApiState();
                    api.dispatch(postLogout());
                }
            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};

export const apiSlice = createApi({
    baseQuery: baseQueryWithReAuth,
    tagTypes: [
        "Auth",
        "OrgItems",
        "OrgCategory",
        "SalesOrgs",
        "SalesCategories",
        "SalesItems",
        "Items",
        "SalesCategoryItems",
        "SalesCategory",
        "SystemLanguages",
        "OrgLanguages",
        "extraInfo",
        "sharedSpec",
        "sharedSpecGroup",
        "kioskSetting",
        "ProductAliases",
        "Table",
        "AmazeLink",
        "ApiKeys",
    ],
    endpoints: () => ({}),
});

export const rtkQueryErrorMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action) && action?.payload?.originalStatus !== 401) {
        // eslint-disable-next-line no-console -- for debugging
        console.log("🐑 ~ fetch api error:", action?.payload?.data || "API ERROR");
    }

    return next(action);
};
